<template>
  <div class="bsp-onboarding">
    <div class="onboarding-card">
      <!-- Header -->
      <div class="card-header">
        <img src="@/assets/whatsapp-svgrepo-com.svg" alt="WhatsApp" class="wa-logo" />
        <h1 class="card-title">Connect WhatsApp Business</h1>
        <p class="card-subtitle">BSP Partner Onboarding</p>
      </div>

      <!-- Idle / Start State -->
      <div v-if="step === 'idle'" class="step-content">
        <div class="info-block">
          <div class="info-row">
            <div class="info-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
            </div>
            <p>You will be redirected to Meta to authorize your WhatsApp Business Account.</p>
          </div>
          <div class="info-row">
            <div class="info-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
            </div>
            <p>Use a Facebook account with <strong>Admin access</strong> on your WhatsApp Business Account.</p>
          </div>
          <div class="info-row">
            <div class="info-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </div>
            <p>Allow popups for this page if your browser asks.</p>
          </div>
        </div>

        <div v-if="errorMessage" class="error-banner">
          {{ errorMessage }}
        </div>

        <button
          @click="startSignup"
          class="start-button"
          :disabled="!fbSdkReady"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-right:8px">
            <path d="M21 2H3v16h5v4l4-4h5l4-4V2z"/>
          </svg>
          {{ fbSdkReady ? 'Connect with Meta' : 'Loading...' }}
        </button>
      </div>

      <!-- Processing State -->
      <div v-else-if="step === 'processing'" class="step-content center">
        <div class="spinner-large"></div>
        <p class="processing-text">Connecting your WhatsApp Business Account...</p>
        <p class="processing-sub">Please wait, do not close this page.</p>
      </div>

      <!-- Success State -->
      <div v-else-if="step === 'success'" class="step-content center">
        <div class="result-icon success">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
            <polyline points="22 4 12 14.01 9 11.01"/>
          </svg>
        </div>
        <h2 class="result-title success">Successfully Connected!</h2>
        <p class="result-message">Your WhatsApp Business Account has been connected. You can now start sending and receiving messages.</p>
        <button @click="goToSettings" class="start-button">Go to WhatsApp Settings</button>
      </div>

      <!-- Error State -->
      <div v-else-if="step === 'error'" class="step-content center">
        <div class="result-icon error">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="15" y1="9" x2="9" y2="15"/>
            <line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
        </div>
        <h2 class="result-title error">Connection Failed</h2>
        <p class="result-message">{{ errorMessage || 'Something went wrong. Please try again.' }}</p>
        <button @click="resetStep" class="start-button error">Try Again</button>
      </div>
    </div>
  </div>
</template>

<script>
import { AuthService } from '@/services/auth'

export default {
  name: 'WhatsAppBSPOnboardingView',

  data () {
    return {
      step: 'idle', // 'idle' | 'processing' | 'success' | 'error'
      errorMessage: '',
      sessionInfoData: null, // { waba_id, phone_number_id } from WA_EMBEDDED_SIGNUP postMessage
      fbSdkReady: false,
      fbAppId: null,
      pendingState: null // state param from /whatsapp-bsp/url, used for inline code exchange
    }
  },

  mounted () {
    window.addEventListener('message', this.handleMessage)
    this.initFBSdk()
  },

  beforeUnmount () {
    window.removeEventListener('message', this.handleMessage)
  },

  methods: {
    initFBSdk () {
      const init = () => {
        const appId = this.fbAppId || '1647999522672796'
        // eslint-disable-next-line no-undef
        FB.init({
          appId: appId,
          autoLogAppEvents: true,
          xfbml: true,
          version: 'v21.0'
        })
        this.fbSdkReady = true
        console.info('[BSP Signup] Facebook SDK initialized')
      }

      // eslint-disable-next-line no-undef
      if (typeof FB !== 'undefined') {
        init()
      } else {
        window.fbAsyncInit = init
      }
    },

    async startSignup () {
      try {
        this.errorMessage = ''
        this.sessionInfoData = null
        this.step = 'processing'

        const token = AuthService.getAuthToken()
        if (!token) throw new Error('Authentication token not found. Please log in again.')

        // Get config_id + state from backend
        const response = await fetch('https://api.quali-d.com/whatsapp-bsp/url', {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`
          }
        })

        if (!response.ok) throw new Error('Failed to initialize signup. Please try again.')

        const data = await response.json()
        if (!data.success || !data.config_id) {
          throw new Error(data.message || 'Invalid configuration received from server.')
        }

        // Store app_id for SDK init fallback
        if (data.app_id) this.fbAppId = data.app_id

        // Store state so FB.login() callback can pass it to the backend for code exchange
        this.pendingState = data.state || null

        // eslint-disable-next-line no-undef
        if (typeof FB === 'undefined' || !this.fbSdkReady) {
          throw new Error('Facebook SDK not ready. Please refresh the page and try again.')
        }

        // Launch Meta embedded signup via FB.login()
        // FB.login() reliably sends the WA_EMBEDDED_SIGNUP postMessage with
        // waba_id + phone_number_id when sessionInfoVersion: 2 is set.
        // eslint-disable-next-line no-undef
        FB.login((loginResponse) => {
          if (!loginResponse || !loginResponse.authResponse) {
            // User dismissed the dialog
            this.step = 'idle'
            this.errorMessage = 'Signup was cancelled. Please try again.'
            return
          }
          const code = loginResponse.authResponse.code
          if (!code) {
            // authResponse present but no code (token mode or unexpected response)
            this.step = 'idle'
            this.errorMessage = 'No authorization code received. Please try again.'
            return
          }
          // The FB SDK delivers the code here via cross-domain bridge.
          // WA_EMBEDDED_SIGNUP postMessage fires just before this callback,
          // so a brief delay ensures sessionInfoData is populated first.
          setTimeout(() => {
            this.completeSignup(code, this.pendingState)
          }, 300)
        }, {
          config_id: data.config_id,
          response_type: 'code',
          override_default_response_type: true,
          extras: {
            setup: { skip: ['pixel', 'ad_account'] },
            featureType: '',
            sessionInfoVersion: '2',
            state: data.state
          }
        })
      } catch (error) {
        console.error('[BSP Signup] Error starting signup:', error)
        this.step = 'error'
        this.errorMessage = error.message || 'Failed to start WhatsApp signup.'
      }
    },

    handleMessage (event) {
      // Meta sends postMessage data as a JSON string — parse it first
      let msgData = event.data
      if (typeof msgData === 'string') {
        try { msgData = JSON.parse(msgData) } catch (e) { return }
      }
      if (!msgData) return

      // 1. Capture Meta's WA_EMBEDDED_SIGNUP session info (phone_number_id + waba_id)
      if (
        (event.origin === 'https://www.facebook.com' || event.origin === 'https://web.facebook.com') &&
        msgData.type === 'WA_EMBEDDED_SIGNUP'
      ) {
        if (msgData.event === 'FINISH') {
          const info = msgData.data || {}
          this.sessionInfoData = {
            waba_id: info.waba_id || null,
            phone_number_id: info.phone_number_id || null
          }
          console.info('[BSP Signup] Session info captured from Meta:', this.sessionInfoData)
        } else if (msgData.event === 'CANCEL') {
          console.info('[BSP Signup] User cancelled the Meta signup flow')
          this.step = 'idle'
        } else if (msgData.event === 'ERROR') {
          console.error('[BSP Signup] Meta reported an error:', msgData)
        }
        return
      }

      // 2. Handle postMessage from our own BSP callback page (fallback path)
      if (event.origin !== window.location.origin) return
      if (msgData.type !== 'whatsapp-bsp-signup') return

      if (msgData.status === 'error') {
        this.step = 'error'
        this.errorMessage = msgData.error || 'Signup failed. Please try again.'
      }
    },

    async completeSignup (code, state) {
      try {
        this.step = 'processing'

        if (!this.sessionInfoData || !this.sessionInfoData.phone_number_id || !this.sessionInfoData.waba_id) {
          throw new Error('WhatsApp account details were not captured from Meta. Please try again.')
        }

        const token = AuthService.getAuthToken()
        if (!token) throw new Error('Authentication token not found.')

        const payload = {
          phone_number_id: this.sessionInfoData.phone_number_id,
          waba_id: this.sessionInfoData.waba_id
        }

        // Include OAuth code so backend can exchange it inline
        // (FB SDK delivers code via cross-domain bridge, not via redirect to callback URL)
        if (code) {
          payload.code = code
          payload.state = state
        }

        const response = await fetch('https://api.quali-d.com/whatsapp-bsp/complete', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        })

        if (!response.ok) {
          let apiError = 'Failed to complete setup. Please try again.'
          try {
            const errBody = await response.json()
            console.error('[BSP Signup] API error response:', response.status, errBody)
            if (errBody.message) apiError = errBody.message
          } catch (e) { /* ignore parse error */ }
          throw new Error(apiError)
        }

        const data = await response.json()
        if (!data.success) throw new Error(data.message || 'Setup could not be completed.')

        this.sessionInfoData = null
        this.pendingState = null
        this.step = 'success'
        console.info('[BSP Signup] Setup completed successfully')
      } catch (error) {
        console.error('[BSP Signup] Error completing signup:', error)
        this.step = 'error'
        this.errorMessage = error.message || 'Failed to complete WhatsApp setup.'
      }
    },

    goToSettings () {
      this.$router.push('/whatsapp-settings')
    },

    resetStep () {
      this.step = 'idle'
      this.errorMessage = ''
      this.sessionInfoData = null
    }
  }
}
</script>

<style scoped>
.bsp-onboarding {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: #f3f4f6;
  padding: 24px;
}

.onboarding-card {
  background: white;
  border-radius: 20px;
  padding: 48px 40px;
  max-width: 520px;
  width: 100%;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
}

.card-header {
  text-align: center;
  margin-bottom: 36px;
}

.wa-logo {
  width: 56px;
  height: 56px;
  margin-bottom: 16px;
}

.card-title {
  font-size: 24px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 6px;
}

.card-subtitle {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.step-content {
  display: flex;
  flex-direction: column;
}

.step-content.center {
  align-items: center;
  text-align: center;
}

.info-block {
  background: #f9fafb;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.info-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.info-icon {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  background: #ede9fe;
  color: #7c3aed;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.info-row p {
  margin: 0;
  font-size: 14px;
  color: #374151;
  line-height: 1.5;
  padding-top: 6px;
}

.error-banner {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  border-radius: 8px;
  padding: 12px 16px;
  font-size: 14px;
  margin-bottom: 16px;
  line-height: 1.5;
}

.start-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 14px 24px;
  background: linear-gradient(135deg, #25D366 0%, #128C7E 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(37, 211, 102, 0.3);
}

.start-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(37, 211, 102, 0.4);
}

.start-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.start-button.error {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
}

.start-button.error:hover {
  box-shadow: 0 8px 20px rgba(220, 38, 38, 0.4);
}

.spinner-large {
  width: 64px;
  height: 64px;
  border: 5px solid #e5e7eb;
  border-top: 5px solid #25D366;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 24px;
}

.processing-text {
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 8px;
}

.processing-sub {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
}

.result-icon {
  width: 96px;
  height: 96px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
}

.result-icon.success {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  color: #059669;
}

.result-icon.error {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  color: #dc2626;
}

.result-title {
  font-size: 22px;
  font-weight: 700;
  margin: 0 0 12px;
}

.result-title.success { color: #059669; }
.result-title.error   { color: #dc2626; }

.result-message {
  font-size: 15px;
  color: #6b7280;
  margin: 0 0 28px;
  line-height: 1.6;
}

@keyframes spin {
  0%   { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@media (max-width: 600px) {
  .onboarding-card { padding: 32px 20px; }
  .card-title { font-size: 20px; }
}
</style>
